/**
 * EinkaufslistePage.jsx – Geteilte Ad-hoc-Einkaufsliste
 * Alle Rollen: anlegen + abhaken. Nur Admin/SL: löschen.
 * Standort-gebunden. Ab Starter-Paket.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Plus, Check, Trash2, Pencil, X, ShoppingCart, MapPin,
  ChevronDown, ChevronUp, Loader2, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ── LocationSelector ──────────────────────────────────────────────────────────
function LocationSelector({ locations, value, onChange }) {
  if (!locations.length) return null;
  if (locations.length === 1) return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <MapPin className="h-3.5 w-3.5 text-primary" />
      <span className="font-medium text-foreground">{locations.find(l => l.id === value)?.name}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-1.5">
      <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
      <select value={value} onChange={e => onChange(e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
        data-testid="location-selector">
        {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
}

// ── Einzelnes Item ─────────────────────────────────────────────────────────────
function ShoppingItem({ item, isConfigurator, onComplete, onReopen, onDelete, onUpdateStockNote }) {
  const [editingNote, setEditingNote] = useState(false);
  const [noteVal, setNoteVal] = useState(item.stock_note || '');
  const [savingNote, setSavingNote] = useState(false);
  const noteInputRef = useRef(null);
  const done = item.status === 'erledigt';

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await onUpdateStockNote(item.id, noteVal);
      setEditingNote(false);
    } finally { setSavingNote(false); }
  };

  useEffect(() => {
    if (editingNote) noteInputRef.current?.focus();
  }, [editingNote]);

  return (
    <div
      className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-all ${
        done
          ? 'border-border bg-muted/20 opacity-60'
          : 'border-border bg-card hover:border-primary/30'
      }`}
      data-testid={`shopping-item-${item.id}`}
    >
      {/* Checkbox */}
      <button
        onClick={() => done ? onReopen(item.id) : onComplete(item.id)}
        className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
          done
            ? 'border-green-500 bg-green-500 text-white'
            : 'border-muted-foreground hover:border-primary'
        }`}
        data-testid={done ? `reopen-${item.id}` : `complete-${item.id}`}
      >
        {done && <Check className="h-3 w-3" />}
      </button>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm ${done ? 'line-through text-muted-foreground' : ''}`}>
          {item.text}
        </p>

        {/* Bestandshinweis */}
        {editingNote ? (
          <div className="flex items-center gap-1.5 mt-1.5">
            <Input
              ref={noteInputRef}
              value={noteVal}
              onChange={e => setNoteVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveNote(); if (e.key === 'Escape') setEditingNote(false); }}
              placeholder="z.B. noch 1 Packung"
              className="h-7 text-xs flex-1"
              data-testid={`stock-note-input-${item.id}`}
            />
            <button onClick={saveNote} disabled={savingNote}
              className="text-primary hover:opacity-75">
              {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </button>
            <button onClick={() => { setEditingNote(false); setNoteVal(item.stock_note || ''); }}
              className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 mt-0.5">
            {item.stock_note ? (
              <p className="text-xs text-muted-foreground">{item.stock_note}</p>
            ) : (
              <p className="text-xs text-muted-foreground/40 italic">kein Bestandshinweis</p>
            )}
            {!done && (
              <button onClick={() => { setEditingNote(true); setNoteVal(item.stock_note || ''); }}
                className="text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                title="Bestandshinweis bearbeiten"
                data-testid={`edit-note-${item.id}`}>
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {done && item.completed_by_name && (
          <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
            <Clock className="h-2.5 w-2.5" />
            {item.completed_by_name} · {new Date(item.completed_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>

      {/* Löschen (nur Admin/SL) */}
      {isConfigurator && (
        <button onClick={() => onDelete(item.id)}
          className="shrink-0 text-muted-foreground/30 hover:text-destructive transition-colors"
          data-testid={`delete-${item.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ── Haupt-Seite ──────────────────────────────────────────────────────────────
export default function EinkaufslistePage() {
  const { user } = useAuth();
  const isConfigurator = ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);

  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState('');
  const [items, setItems] = useState([]);
  const [doneItems, setDoneItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);

  // Formular
  const [text, setText] = useState('');
  const [stockNote, setStockNote] = useState('');
  const [adding, setAdding] = useState(false);

  // Standorte laden
  useEffect(() => {
    axios.get(`${API}/api/user/locations`, { headers: authH() })
      .then(r => {
        const locs = r.data || [];
        setLocations(locs);
        if (locs.length > 0) {
          const own = user?.location_ids?.length ? locs.find(l => user.location_ids.includes(l.id)) : null;
          setSelectedLocId(own?.id || locs[0].id);
        }
      }).catch(() => toast.error('Standorte konnten nicht geladen werden'));
  }, [user?.id]);

  // Items laden
  const fetchItems = useCallback(async () => {
    if (!selectedLocId) return;
    setLoading(true);
    try {
      const [openRes, doneRes] = await Promise.all([
        axios.get(`${API}/api/shopping-list?location_id=${selectedLocId}&include_done=false`, { headers: authH() }),
        axios.get(`${API}/api/shopping-list?location_id=${selectedLocId}&include_done=true`, { headers: authH() }),
      ]);
      setItems(openRes.data || []);
      setDoneItems((doneRes.data || []).filter(i => i.status === 'erledigt').sort((a, b) =>
        (b.completed_at || '').localeCompare(a.completed_at || '')
      ).slice(0, 20));
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, [selectedLocId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/api/shopping-list`, {
        location_id: selectedLocId, text: text.trim(), stock_note: stockNote.trim() || null,
      }, { headers: authH() });
      setText(''); setStockNote('');
      fetchItems();
      toast.success('Hinzugefügt!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    finally { setAdding(false); }
  };

  const handleComplete = async (id) => {
    try { await axios.post(`${API}/api/shopping-list/${id}/complete`, {}, { headers: authH() }); fetchItems(); }
    catch { toast.error('Fehler'); }
  };

  const handleReopen = async (id) => {
    try { await axios.post(`${API}/api/shopping-list/${id}/reopen`, {}, { headers: authH() }); fetchItems(); }
    catch { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/api/shopping-list/${id}`, { headers: authH() }); fetchItems(); }
    catch { toast.error('Fehler'); }
  };

  const handleUpdateStockNote = async (id, note) => {
    await axios.patch(`${API}/api/shopping-list/${id}/stock-note`, { stock_note: note }, { headers: authH() });
    fetchItems();
    toast.success('Bestand aktualisiert');
  };

  const sharedProps = { isConfigurator, onComplete: handleComplete, onReopen: handleReopen, onDelete: handleDelete, onUpdateStockNote: handleUpdateStockNote };

  return (
    <DashboardLayout>
      <div className="max-w-xl mx-auto px-4 pb-8 space-y-4 pt-2" data-testid="einkaufsliste-page">

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-primary" />
              Einkaufsliste
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">Für externe Einkäufe — alle können eintragen & abhaken</p>
          </div>
          <LocationSelector locations={locations} value={selectedLocId} onChange={setSelectedLocId} />
        </div>

        {/* Formular */}
        <form onSubmit={handleAdd} className="space-y-2" data-testid="add-item-form">
          <div className="flex gap-2">
            <Input
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Was brauchen wir? (z.B. Tomaten)"
              className="flex-1 h-10"
              data-testid="item-text-input"
              autoComplete="off"
            />
            <Button type="submit" disabled={adding || !text.trim()} className="gap-1.5 shrink-0 h-10"
              data-testid="add-item-btn">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Hinzufügen
            </Button>
          </div>
          <Input
            value={stockNote}
            onChange={e => setStockNote(e.target.value)}
            placeholder="Aktueller Bestand (optional) — z.B. 'noch 1 Packung'"
            className="h-9 text-sm"
            data-testid="stock-note-input"
          />
        </form>

        {/* Offene Items */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground" data-testid="empty-state">
            <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium text-sm">Liste ist leer</p>
            <p className="text-xs mt-1">Trag oben ein, was beim nächsten Einkauf besorgt werden muss.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="open-items-list">
            {items.map(item => (
              <ShoppingItem key={item.id} item={item} {...sharedProps} />
            ))}
          </div>
        )}

        {/* Erledigte Items (eingeklappt) */}
        {doneItems.length > 0 && (
          <div className="border border-border rounded-xl overflow-hidden">
            <button onClick={() => setShowDone(v => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/30 hover:bg-muted/50 transition-colors"
              data-testid="toggle-done-btn">
              <span className="text-sm text-muted-foreground">
                Kürzlich erledigt ({doneItems.length})
              </span>
              {showDone ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {showDone && (
              <div className="divide-y divide-border/40 p-2 space-y-1.5" data-testid="done-items-list">
                {doneItems.map(item => (
                  <ShoppingItem key={item.id} item={item} {...sharedProps} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
